import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useClarificationNavigation } from '../ClarificationNavigationContext';
import {
  ClarificationTarget,
  hiddenOpenText,
  stepIndex,
} from '../clarificationTargets';

const describeTarget = (target: ClarificationTarget) =>
  `${target.topLevelSectionLabel} · ${target.questionPrompt}`;

/**
 * Fixed bar over the form column for stepping through fields with open
 * clarifications. Fixed rather than sticky: the form's ancestors set
 * overflow-x-hidden, which makes sticky positioning a no-op.
 */
export const ClarificationNavigator = ({
  withSidebar,
}: {
  /** Leave room for the fixed sidebar at lg+ */
  withSidebar: boolean;
}) => {
  const {
    enabled,
    targets,
    hiddenUnresolvedCount,
    cursorFor,
    currentSlug,
    goTo,
    step,
  } = useClarificationNavigation();
  const { totalClarificationCount } = useClarificationsContext();

  if (!enabled || totalClarificationCount === 0) return null;

  const cursor = cursorFor(currentSlug);
  const onTarget = Number.isInteger(cursor);
  const openThreads = targets.reduce((sum, t) => sum + t.unresolvedCount, 0);
  const notStarted = currentSlug === null;
  // Only resolved when nothing is open anywhere — threads on questions hidden
  // by branching are still open on the server.
  const allResolved = targets.length === 0 && hiddenUnresolvedCount === 0;

  let summary: string;
  if (allResolved) {
    summary = 'All clarifications resolved';
  } else if (targets.length === 0) {
    summary = `Open clarifications: ${hiddenOpenText(hiddenUnresolvedCount)}`;
  } else {
    summary = `${openThreads} open clarification${openThreads === 1 ? '' : 's'}`;
    if (openThreads !== targets.length) {
      summary += ` on ${targets.length} fields`;
    }
    if (hiddenUnresolvedCount > 0) {
      summary += ` · ${hiddenOpenText(hiddenUnresolvedCount)}`;
    }
  }

  let detail: string | null = null;
  if (targets.length === 0 && !allResolved) {
    detail = 'Hidden by an answer elsewhere in the form';
  } else if (targets.length > 0) {
    detail = onTarget
      ? describeTarget(targets[cursor])
      : `Next: ${describeTarget(targets[stepIndex(cursor, targets.length, 'next')])}`;
  }

  return (
    <div
      className={`fixed top-22 inset-x-0 z-40 flex justify-center px-6 pointer-events-none print:hidden ${withSidebar ? 'lg:left-80' : ''}`}
    >
      <div
        role="navigation"
        aria-label="Clarifications"
        className={`pointer-events-auto w-full max-w-4xl flex items-center gap-3 rounded-lg border px-4 py-2 shadow-sm ${
          allResolved
            ? 'bg-green-50 border-green-200'
            : 'bg-white border-amber-200'
        }`}
      >
        {allResolved ? (
          <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />
        ) : (
          <MessageSquare className="h-4 w-4 flex-shrink-0 text-amber-600" />
        )}
        <div className="flex-1 min-w-0" aria-live="polite" aria-atomic="true">
          <p
            className={`text-sm font-medium ${allResolved ? 'text-green-700' : 'text-gray-900'}`}
          >
            {summary}
          </p>
          {detail && (
            <p className="text-xs text-gray-500 truncate" title={detail}>
              {detail}
            </p>
          )}
        </div>

        {targets.length > 0 &&
          (notStarted ? (
            <Button
              type="button"
              size="sm"
              onClick={() => goTo(0)}
              className="flex-shrink-0 bg-primary-500 hover:bg-primary-600 text-white"
            >
              Go to first
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <div className="flex flex-shrink-0 items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => step('prev')}
                aria-label="Previous clarification"
                className="shadow-none"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Prev</span>
              </Button>
              <span className="min-w-14 text-center text-xs text-gray-600 tabular-nums">
                {onTarget ? `${cursor + 1} of ${targets.length}` : '–'}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => step('next')}
                aria-label="Next clarification"
                className="shadow-none"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ))}
      </div>
    </div>
  );
};
